// i18next setup. Language = Settings → Language, or the device language when "System".
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en';
import de from './de';

/** Swiss Standard German never uses "ß". */
function swiss<T>(value: T): T {
  if (typeof value === 'string') return value.replace(/ß/g, 'ss') as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, swiss(v)])) as T;
  }
  return value;
}

export const resources = {
  en: { translation: en },
  de: { translation: de },
  'de-CH': { translation: swiss(de) },
  'de-AT': { translation: { grocery: { title: 'Einkaufszettel' } } },
} as const;

export function deviceLanguage(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale || 'en';
  } catch {
    return 'en';
  }
}

/** Maps a setting ('system' | 'de-CH' | …) to an i18next language code. */
export function resolveLanguage(setting: string): string {
  const lang = setting === 'system' ? deviceLanguage() : setting;
  if (lang.startsWith('de-CH')) return 'de-CH';
  if (lang.startsWith('de-AT')) return 'de-AT';
  if (lang.startsWith('de')) return 'de';
  return 'en';
}

i18n.use(initReactI18next).init({
  resources,
  lng: resolveLanguage('system'),
  fallbackLng: { 'de-CH': ['en'], 'de-AT': ['de', 'en'], default: ['en'] },
  interpolation: { escapeValue: false },
  initAsync: false,
  returnNull: false,
});

/** BCP-47 locale for dates & numbers. */
export function locale(): string {
  const l = i18n.language;
  return l === 'de' ? 'de-DE' : l === 'en' ? 'en-US' : l;
}

export default i18n;

declare module 'i18next' {
  interface CustomTypeOptions {
    resources: { translation: typeof en };
    returnNull: false;
  }
}
